import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { RefreshCw, Eye, Users, FileBarChart2, Layers, Search, ChevronLeft, ChevronRight, Info } from 'lucide-react';
import { Button } from '../../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

const RANGES = [
  { label: '24h', days: 1 },
  { label: '7d',  days: 7 },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
];

const ROLE_LABELS = {
  tf_admin: 'TF Admin', tf_reporting_admin: 'TF Reporting Admin', tf_report_viewer: 'TF Report Viewer',
  ms_exec: 'MS Executive / FTE', ms_vendor: 'MS Vendor', ms_pdm: 'MS PDM',
  ms_prog_admin: 'Program / Project Admin', unassigned: 'Unassigned',
};

function fmtTime(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

function KpiCard({ icon: Icon, label, value }) {
  return (
    <div
      data-testid={`audit-kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}
      style={{ background: '#fff', borderRadius: 10, padding: '20px 24px', border: '1px solid #e5e7eb', flex: 1, minWidth: 180 }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <div style={{ width: 34, height: 34, borderRadius: 8, background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={16} color="#16a34a" />
        </div>
        <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>{label}</span>
      </div>
      <div style={{ fontSize: 32, fontWeight: 800, color: '#111827' }}>{(value ?? 0).toLocaleString()}</div>
    </div>
  );
}

function RoleBadge({ role }) {
  if (!role) return <span style={{ color: '#9ca3af' }}>—</span>;
  const isTf = String(role).startsWith('tf_');
  const color = isTf ? '#194449' : '#7c3aed';
  return (
    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: color + '18', color }}>
      {ROLE_LABELS[role] || role}
    </span>
  );
}

const thStyle = { textAlign: 'left', padding: '10px 14px', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid #e5e7eb' };
const tdStyle = { padding: '10px 14px', fontSize: 13, color: '#374151', borderBottom: '1px solid #f3f4f6' };

export default function AccessAuditTrail() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [range, setRange]     = useState(RANGES[1]); // 7d
  const [groupId, setGroupId] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch]   = useState('');
  const [page, setPage]       = useState(1);
  const perPage = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { days: range.days, page, per_page: perPage };
      if (groupId) params.group_id = groupId;
      if (search)  params.search = search;
      const res = await axios.get(`${API}/api/admin/portal/access-audit`, { params, withCredentials: true });
      setData(res.data);
    } catch (err) {
      console.error('Access audit fetch failed:', err?.response?.status, err?.response?.data || err.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [range, groupId, search, page]);

  useEffect(() => { load(); }, [load]);

  const kpi        = data?.kpi || {};
  const groups     = data?.groups || [];
  const reports    = data?.reports || [];
  const allGroups  = data?.all_groups || [];
  const log        = data?.log?.data || [];
  const total      = data?.log?.total || 0;
  const rbacOk     = data?.rbac_available;
  const totalPages = Math.max(1, Math.ceil(total / perPage));

  const submitSearch = () => { setPage(1); setSearch(searchInput.trim()); };

  return (
    <div style={{ padding: '28px 32px', fontFamily: '"DM Sans", sans-serif' }} data-testid="access-audit-page">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#0f2a35', margin: 0 }}>Access Audit Trail</h1>
          <p style={{ color: '#6b7280', fontSize: 14, marginTop: 4 }}>
            Who accessed which report, and when — grouped by portal group ({data?.range_label || `last ${range.days}d`}).
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div data-testid="audit-range-selector" style={{ display: 'flex', gap: 3, background: '#f3f4f6', borderRadius: 8, padding: 3 }}>
            {RANGES.map(r => (
              <button
                key={r.label}
                data-testid={`audit-range-${r.label}`}
                onClick={() => { setPage(1); setRange(r); }}
                style={{
                  padding: '4px 12px', fontSize: 12, fontWeight: 600, borderRadius: 6, border: 'none', cursor: 'pointer',
                  background: range.label === r.label ? '#194449' : 'transparent',
                  color: range.label === r.label ? '#fff' : '#6b7280',
                }}
              >
                {r.label}
              </button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={load} data-testid="audit-refresh-btn" disabled={loading} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <RefreshCw size={13} style={loading ? { animation: 'spin 1s linear infinite' } : {}} />
            Refresh
          </Button>
        </div>
      </div>

      {/* RBAC banner */}
      {data && !rbacOk && (
        <div data-testid="audit-rbac-banner" style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', borderRadius: 8, padding: '10px 14px', marginBottom: 18, fontSize: 13 }}>
          <Info size={15} />
          Group attribution is unavailable for this session (sign in with your @technofocus.co SSO admin account to see per-group data). The access log below still shows who viewed what and when.
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        <select
          data-testid="audit-group-filter"
          value={groupId}
          onChange={e => { setPage(1); setGroupId(e.target.value); }}
          disabled={!rbacOk || allGroups.length === 0}
          style={{ padding: '8px 12px', fontSize: 13, borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', color: '#374151', minWidth: 220 }}
        >
          <option value="">All groups</option>
          {allGroups.map(g => (
            <option key={g.group_id} value={g.group_id}>{g.group_name}</option>
          ))}
        </select>
        <div style={{ display: 'flex', gap: 6 }}>
          <div style={{ position: 'relative' }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: 10, color: '#9ca3af' }} />
            <input
              data-testid="audit-search-input"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') submitSearch(); }}
              placeholder="Search by email…"
              style={{ padding: '8px 12px 8px 30px', fontSize: 13, borderRadius: 8, border: '1px solid #e5e7eb', minWidth: 240 }}
            />
          </div>
          <Button variant="outline" size="sm" onClick={submitSearch} data-testid="audit-search-btn">Search</Button>
          {(search || groupId) && (
            <Button variant="ghost" size="sm" data-testid="audit-clear-btn" onClick={() => { setSearchInput(''); setSearch(''); setGroupId(''); setPage(1); }}>Clear</Button>
          )}
        </div>
      </div>

      {/* KPIs */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }} data-testid="audit-kpi-row">
        <KpiCard icon={Eye}            label="Total Report Views" value={kpi.total_views} />
        <KpiCard icon={Users}          label="Unique Users"       value={kpi.unique_users} />
        <KpiCard icon={FileBarChart2}  label="Reports Accessed"   value={kpi.reports_accessed} />
        <KpiCard icon={Layers}         label="Active Groups"      value={kpi.active_groups} />
      </div>

      {/* Two-column: per-group + per-report */}
      <div style={{ display: 'flex', gap: 20, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 320, background: '#fff', borderRadius: 10, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
          <h2 style={{ fontSize: 15, fontWeight: 700, color: '#111827', margin: 0, padding: '16px 18px' }}>By Group</h2>
          <table style={{ width: '100%', borderCollapse: 'collapse' }} data-testid="audit-groups-table">
            <thead><tr><th style={thStyle}>Group</th><th style={thStyle}>Role</th><th style={{ ...thStyle, textAlign: 'right' }}>Views</th><th style={{ ...thStyle, textAlign: 'right' }}>Users</th></tr></thead>
            <tbody>
              {groups.length === 0 ? (
                <tr><td style={{ ...tdStyle, color: '#9ca3af' }} colSpan={4}>No group access data.</td></tr>
              ) : groups.map((g, i) => (
                <tr key={i} data-testid={`audit-group-row-${i}`}>
                  <td style={{ ...tdStyle, fontWeight: 600, color: '#111827' }}>{g.group_name}</td>
                  <td style={tdStyle}><RoleBadge role={g.group_role} /></td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700 }}>{(g.views ?? 0).toLocaleString()}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{(g.unique_users ?? 0).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ flex: 1, minWidth: 320, background: '#fff', borderRadius: 10, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
          <h2 style={{ fontSize: 15, fontWeight: 700, color: '#111827', margin: 0, padding: '16px 18px' }}>By Report</h2>
          <table style={{ width: '100%', borderCollapse: 'collapse' }} data-testid="audit-reports-table">
            <thead><tr><th style={thStyle}>Report</th><th style={{ ...thStyle, textAlign: 'right' }}>Views</th><th style={{ ...thStyle, textAlign: 'right' }}>Users</th></tr></thead>
            <tbody>
              {reports.length === 0 ? (
                <tr><td style={{ ...tdStyle, color: '#9ca3af' }} colSpan={3}>No report access data.</td></tr>
              ) : reports.map((r, i) => (
                <tr key={i} data-testid={`audit-report-row-${i}`}>
                  <td style={{ ...tdStyle, fontWeight: 600, color: '#111827' }}>{r.report}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700 }}>{(r.views ?? 0).toLocaleString()}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{(r.unique_users ?? 0).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Access log */}
      <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 18px' }}>
          <h2 style={{ fontSize: 15, fontWeight: 700, color: '#111827', margin: 0 }}>Access Log</h2>
          <span style={{ fontSize: 12, color: '#9ca3af' }}>{total.toLocaleString()} entries</span>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }} data-testid="audit-log-table">
          <thead><tr><th style={thStyle}>When</th><th style={thStyle}>User</th><th style={thStyle}>Group</th><th style={thStyle}>Report</th></tr></thead>
          <tbody>
            {log.length === 0 ? (
              <tr><td style={{ ...tdStyle, color: '#9ca3af' }} colSpan={4}>No access recorded for this filter.</td></tr>
            ) : log.map((row, i) => (
              <tr key={i} data-testid={`audit-log-row-${i}`}>
                <td style={{ ...tdStyle, whiteSpace: 'nowrap', color: '#6b7280' }}>{fmtTime(row.accessed_at)}</td>
                <td style={{ ...tdStyle, fontWeight: 600, color: '#111827' }}>{row.user_email}</td>
                <td style={tdStyle}>{row.group_name || <span style={{ color: '#9ca3af' }}>—</span>}</td>
                <td style={tdStyle}>{row.report}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {/* Pagination */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12, padding: '12px 18px', borderTop: '1px solid #f3f4f6' }}>
          <span style={{ fontSize: 12, color: '#6b7280' }}>Page {page} of {totalPages}</span>
          <Button variant="outline" size="sm" data-testid="audit-prev-btn" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <ChevronLeft size={14} /> Prev
          </Button>
          <Button variant="outline" size="sm" data-testid="audit-next-btn" disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            Next <ChevronRight size={14} />
          </Button>
        </div>
      </div>
    </div>
  );
}
